import type { ReactElement } from 'react'

/**
 * Level to tag, as a lookup rather than a ternary chain.
 *
 * `1` exists because the biography route's own title is this component — an
 * eyebrow above a name above a rule is exactly the lockup it needs, and there is
 * no reason for a route to reimplement it just to be the top of its outline.
 */
const HEADING_TAGS = { 1: 'h1', 2: 'h2', 3: 'h3' } as const

export interface SectionHeadingProps {
  /**
   * Heading level, matching the `PoemArticle.headingLevel` convention: where a
   * heading sits in the outline is the page's business, not the component's.
   */
  readonly level: 1 | 2 | 3
  /** Uppercase, letter-spaced kicker above the title. */
  readonly eyebrow?: string
  readonly title: string
  /** Quiet trailing note — `23 poemas`, `5 poemas`, `de A a Z`. */
  readonly note?: string
  /** Forwarded so the enclosing section can be `aria-labelledby` this heading. */
  readonly id?: string
}

/**
 * Eyebrow, title, optional note, and a hairline that fills the remaining space.
 *
 * The rule is `aria-hidden` and uses the decorative `--line` token, not
 * `--line-strong`: it is ornament, so § 1.4.11 does not apply to it.
 */
export function SectionHeading({
  level,
  eyebrow,
  title,
  note,
  id,
}: SectionHeadingProps): ReactElement {
  const Heading = HEADING_TAGS[level]

  return (
    <div className={`section-heading section-heading--level-${String(level)}`}>
      {eyebrow === undefined ? null : (
        <p className="section-heading__eyebrow">{eyebrow}</p>
      )}

      <div className="section-heading__row">
        <Heading className="section-heading__title" id={id}>
          {title}
        </Heading>
        {note === undefined ? null : <span className="section-heading__note">{note}</span>}
        <span className="section-heading__rule" aria-hidden="true" />
      </div>
    </div>
  )
}
